import React, { useState, useEffect, useRef } from "react";
import AdminAppBar from "../../adminComponents/AdminAppBar";
import BackArrows from "../../Form/BackArrow/BackArrows";
import { useLocation, useNavigate } from "react-router-dom";
import { showWarningToast } from "../../Constant/Notification";
import CPManageService from "../../Services/CPManageService";
import { getIcons } from "../../Constant/Constant";
import { ToastContainer } from "react-toastify";

const UnassignLeadsList = () => {
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [leadData, setLeadData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextPageId, setNextPageId] = useState(null);
  const [previousPageId, setPreviousPageId] = useState(null);

  const cachedData = useRef({});

  const location = useLocation();
  const { state } = location;
  const section = state?.section || "";
  const count = state?.count || 0;
  const leadType = state?.type || false;
  const icons = getIcons();

  const navigate = useNavigate();
  const userObj = JSON.parse(localStorage.getItem("userData") || "{}");
  const userRole = userObj?.user?.role || "Admin";

  const getAssignedLeads = async (pageId, direction) => {
    if (direction === "previous" && cachedData.current[pageId]) {
      const cachedPage = cachedData.current[pageId];
      setLeadData(cachedPage.leads);
      setNextPageId(cachedPage.nextPage);
      setPreviousPageId(cachedPage.previousPage);
      return;
    }

    try {
      setLoading(true);
      const response = await CPManageService.CPAssignedLeads(
        leadType,
        pageId,
        direction
      );

      const leads = response.data?.leads || [];
      setLeadData(leads);
      setNextPageId(response.data?.nextPage);
      setPreviousPageId(response.data?.previousPage);

      const cacheKey = pageId || "first";
      cachedData.current[cacheKey] = {
        leads: leads,
        nextPage: response.data?.nextPage,
        previousPage: response.data?.previousPage,
      };
    } catch (error) {
      showWarningToast("Error fetching leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cachedData.current = {};
    setCurrentPage(1);
    getAssignedLeads(null, null);
  }, [leadType]);

  const handlePageNavigation = (pageId, direction) => {
    if (direction === "next" && !nextPageId) return;
    if (direction === "previous" && !previousPageId) return;

    getAssignedLeads(pageId, direction);

    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === "previous") {
      setCurrentPage((prev) => Math.max(1, prev - 1));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAllChange = () => {
    if (selectedLeads.length === leadData.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leadData.map((lead) => lead.id));
    }
  };

  const handleAssignMember = () => {
    if (selectedLeads.length === 0) {
      showWarningToast("Please select at least one lead to assign");
      return;
    }
    const path = userRole === "Admin" ? "/admin" : "/cmo";
    navigate(`${path}/cp-assign-team-member`, {
      state: { selectedLeads },
    });
  };

  const isAllSelected =
    selectedLeads.length === leadData.length && leadData.length > 0;
  const showPaginationArrows = leadData.length >= 10;

  return (
    <div className="bg-appGrayBackground min-h-screen pb-16">
      <AdminAppBar />
      <ToastContainer />
      <div className="mt-20 pt-8 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <BackArrows onClick={handleBack} />
            <h1 className="text-[32px] font-semibold">
              {section === "unassign-lead"
                ? "Unassign Leads"
                : "Unassigned Leads with Followups"}
            </h1>
          </div>
        </div>

        <div className="flex items-center justify-between gap-8 mt-[50px] flex-wrap">
          <div className="flex items-center justify-start gap-8 flex-wrap">
            <div className="border-2 bg-white text-[20px] font-semibold px-4 py-3 rounded-2xl">
              Total Count: {count}
            </div>

            <div className="border-2 text-customGreen bg-white text-[20px] font-semibold px-4 py-3 rounded-2xl">
              Selected Count: {selectedLeads.length}
            </div>
          </div>

          <button
            onClick={handleAssignMember}
            disabled={selectedLeads.length === 0}
            className={`border-2 ${
              selectedLeads.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-customGreen cursor-pointer hover:bg-green-600"
            } text-white text-[20px] font-semibold px-4 py-3 rounded-2xl transition-colors`}
          >
            Assign To Member
          </button>
        </div>

        <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-[58px]">
          <table className="w-full text-sm text-left rtl:text-right text-gray-500 ">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 ">
              <tr>
                <th scope="col" className="p-4">
                  <div className="flex items-center">
                    <input
                      id="checkbox-all"
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAllChange}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm cursor-pointer"
                    />
                    <label htmlFor="checkbox-all" className="sr-only">
                      Select All
                    </label>
                  </div>
                </th>
                <th scope="col" className="px-6 py-3">
                  Name
                </th>
                <th scope="col" className="px-6 py-3">
                  Phone Number
                </th>
                <th scope="col" className="px-6 py-3">
                  Interested Location
                </th>
                {section === "unassign-lead-followup" && (
                  <th scope="col" className="px-6 py-3">
                    Follow-up Date
                  </th>
                )}
                <th scope="col" className="px-6 py-3">
                  Added Date
                </th>
              </tr>
            </thead>
            <tbody>
              {leadData.map((list) => (
                <tr
                  key={list.id}
                  className="bg-white border-b  border-gray-200 hover:bg-gray-50 "
                >
                  <td className="w-4 p-4">
                    <div className="flex items-center">
                      <input
                        id={`checkbox-${list.id}`}
                        type="checkbox"
                        checked={selectedLeads.includes(list.id)}
                        onChange={() => handleCheckboxChange(list.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm cursor-pointer"
                      />
                      <label
                        htmlFor={`checkbox-${list.id}`}
                        className="sr-only"
                      >
                        checkbox
                      </label>
                    </div>
                  </td>
                  <th
                    scope="row"
                    className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap "
                  >
                    {list?.leadName}
                  </th>
                  <td className="px-6 py-4">{list?.phoneNumber}</td>
                  <td className="px-6 py-4 cursor-pointer" title={list.interestedLocation}>
                    {Array.isArray(list?.interestedLocation) &&
                    list.interestedLocation.length > 0 ? (
                      <>
                        {list.interestedLocation[0]}
                        {list.interestedLocation.length > 1 && (
                          <span
                            title={list.interestedLocation}
                            className="text-blue-500"
                          >
                            {" "}
                            +{list.interestedLocation.length - 1}
                          </span>
                        )}
                      </>
                    ) : (
                      "No Location"
                    )}
                  </td>

                  {section === "unassign-lead-followup" && (
                    <td className="px-6 py-4">{list?.cpFollowUpDate}</td>
                  )}
                  <td className="px-6 py-4">{list.AddedDate}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-center my-5 gap-10">
            {showPaginationArrows && previousPageId && (
              <button
                onClick={() => handlePageNavigation(previousPageId, "previous")}
                className="mx-1 px-3 py-1 border rounded text-blue-500"
                disabled={loading}
              >
                <img
                  src={icons.previousPagination}
                  className="w-3"
                  alt="Previous"
                />
              </button>
            )}

            <span>{currentPage}</span>

            {showPaginationArrows && nextPageId && (
              <button
                onClick={() => handlePageNavigation(nextPageId, "next")}
                className="mx-1 px-3 py-1 border rounded text-blue-500"
                disabled={loading}
              >
                <img src={icons.nextPagination} className="w-3" alt="Next" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnassignLeadsList;
